import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ecourtApi } from './ecourtApi';
import { ecPath, fmtDate, caseTitle, statusBadge } from './ecourtUtils';
import { Loader, ErrorBox, Empty, PageHeader, Segmented, Cost, ChargeNote, useBalance } from './EcourtUi';

function ChipField({ id, label, values, onChange, placeholder, col = 'col-12 col-md-4' }) {
  const [text, setText] = useState('');
  const add = () => {
    const v = text.trim();
    if (v && !values.includes(v) && values.length < 10) onChange([...values, v]);
    setText('');
  };
  return (
    <div className={col}>
      <label htmlFor={id} className="ec-label">{label}</label>
      <div className="ec-chipbox">
        {values.map((v) => (
          <span className="ec-chip" key={v}>
            {v}
            <button type="button" aria-label={`Remove ${v}`} onClick={() => onChange(values.filter((x) => x !== v))}>×</button>
          </span>
        ))}
        <input id={id} className="ec-chip-input" value={text} placeholder={values.length ? 'add another (OR)' : placeholder}
          onChange={(e) => setText(e.target.value)} onBlur={add}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} />
      </div>
    </div>
  );
}

const SEARCH_MODES = [
  { value: 'number', label: 'Case number' },
  { value: 'keyword', label: 'Keyword & names' },
];

const MATCH_MODES = [
  { value: 'all', label: 'All words' },
  { value: 'phrase', label: 'Exact' },
  { value: 'fuzzy', label: 'Fuzzy' },
  { value: 'any', label: 'Any word' },
];

const STATUSES = [
  { value: '', label: 'All' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'DISPOSED', label: 'Disposed' },
];

const CASE_TYPES = ['CIVIL', 'CRIMINAL', 'WRIT', 'APPEAL', 'REVISION', 'EXECUTION', 'ARBITRATION', 'MATRIMONIAL', 'MOTOR_ACCIDENT', 'LABOR'];

const SORTS = [
  { value: '', label: 'Best match' },
  { value: 'nextHearingDate|asc', label: 'Next hearing (soonest)' },
  { value: 'filingDate|desc', label: 'Filed (newest)' },
  { value: 'filingDate|asc', label: 'Filed (oldest)' },
  { value: 'decisionDate|desc', label: 'Decided (newest)' },
];

/**
 * Facet sidebar groups. A group shows only if the search response has that facet.
 * Clicking a value sets the matching search filter (all of these are in the backend's SEARCH_ARRAYS).
 */
const FACET_GROUPS = [
  { facet: 'stateCode', param: 'stateCodes', title: 'State' },
  { facet: 'courtCode', param: 'courtCodes', title: 'Court' },
  { facet: 'caseStatus', param: 'caseStatuses', title: 'Status' },
  { facet: 'caseType', param: 'caseTypes', title: 'Case type' },
];

const list = (a) => (Array.isArray(a) && a.length ? a.join(', ') : '');

export default function CaseSearch() {
  const { note } = useBalance();

  const [searchMode, setSearchMode] = useState('number');
  // Case number tab
  const [caseNo, setCaseNo] = useState('');
  // Keyword tab
  const [query, setQuery] = useState('');
  const [section, setSection] = useState('');
  const [litigants, setLitigants] = useState([]);
  const [advocates, setAdvocates] = useState([]);
  const [judges, setJudges] = useState([]);
  const [mode, setMode] = useState('all');
  // Shared filters
  const [status, setStatus] = useState('');
  const [caseType, setCaseType] = useState('');
  const [filingYear, setFilingYear] = useState('');
  const [sort, setSort] = useState('');
  const [pageSize, setPageSize] = useState(50);
  const [picked, setPicked] = useState({}); // facet filters: { stateCodes: 'TN', courtCodes: 'TNCH01' }
  const [showFilters, setShowFilters] = useState(false);

  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const run = async (opts = {}) => {
    const p = opts.page || 1;
    const st = opts.status !== undefined ? opts.status : status;
    const ct = opts.caseType !== undefined ? opts.caseType : caseType;
    const fp = opts.picked !== undefined ? opts.picked : picked;

    const params = { page: p, pageSize };
    if (searchMode === 'number') {
      const n = caseNo.trim().replace(/\s+/g, ' ');
      if (!n) { setError({ message: 'Type a case number, e.g. OS/1617/2020.' }); return; }
      params.query = n;
    } else {
      if (!query.trim() && !litigants.length && !advocates.length && !judges.length && !section.trim()) {
        setError({ message: 'Type a keyword, a section or add at least one name.' });
        return;
      }
      Object.assign(params, {
        query: query.trim(), litigants, advocates, judges, nameMatchMode: mode,
        actsAndSections: section.trim() ? [section.trim()] : undefined,
      });
    }
    if (st) params.caseStatuses = [st];
    if (ct) params.caseTypes = [ct];
    if (/^\d{4}$/.test(filingYear)) {
      params.filingDateFrom = `${filingYear}-01-01`;
      params.filingDateTo = `${filingYear}-12-31`;
    }
    if (sort) { const [by, ord] = sort.split('|'); params.sortBy = by; params.sortOrder = ord; }
    Object.entries(fp).forEach(([k, v]) => { if (v) params[k] = [v]; });

    setLoading(true);
    setError(null);
    try {
      const res = await ecourtApi.search(params);
      setData(note(res));
      setPage(p);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (e) => { e.preventDefault(); run({ page: 1 }); };

  const pickFacet = (param, value) => {
    if (param === 'caseStatuses') { const v = status === value ? '' : value; setStatus(v); run({ page: 1, status: v }); return; }
    if (param === 'caseTypes') { const v = caseType === value ? '' : value; setCaseType(v); run({ page: 1, caseType: v }); return; }
    const next = { ...picked, [param]: picked[param] === value ? '' : value };
    setPicked(next);
    run({ page: 1, picked: next });
  };
  const isPicked = (param, value) => (param === 'caseStatuses' ? status === value : param === 'caseTypes' ? caseType === value : picked[param] === value);

  const switchMode = (m) => { setSearchMode(m); setError(null); };

  const lookup = (data && data.enumDescriptions && data.enumDescriptions.enumLookup) || {};
  const label = (field, code) => (lookup[field] && lookup[field][code]) || code;
  const facets = (data && data.facets) || {};
  /**
   * Court list: the API facet when it sends one; otherwise counted from the results on this page
   * (every result carries courtCode). Court names come from enumLookup when available.
   */
  const pageCourts = (() => {
    if (facets.courtCode && facets.courtCode.values) return null;
    const counts = {};
    ((data && data.results) || []).forEach((r) => { if (r.courtCode) counts[r.courtCode] = (counts[r.courtCode] || 0) + 1; });
    if (picked.courtCodes && !counts[picked.courtCodes]) counts[picked.courtCodes] = 0;
    return counts;
  })();
  const facetValues = (name) => {
    const values = name === 'courtCode' && pageCourts ? pageCourts : ((facets[name] && facets[name].values) || {});
    return Object.entries(values).sort((a, b) => b[1] - a[1]).slice(0, name === 'courtCode' ? 25 : 10);
  };
  const courtsFromPage = Boolean(pageCourts) && data && Number(data.totalHits || 0) > ((data.results || []).length);
  const total = Number((data && data.totalHits) || 0);
  // v2: when facets were skipped for a very broad search, totalHits is a lower bound (100000)
  const totalLabel = `${total.toLocaleString('en-IN')}${data && data.facetMode === 'full' && total >= 100000 ? '+' : ''}`;
  const searchedFor = searchMode === 'number' ? caseNo.trim() : query.trim();

  return (
    <div>
      <PageHeader title="Case Search" subtitle="Supreme Court, all High Courts, district courts and tribunals in one search." />

      <form className="ec-card mb-3" onSubmit={onSubmit}>
        <div className="mb-3">
          <Segmented value={searchMode} options={SEARCH_MODES} onChange={switchMode} label="Search by" />
        </div>

        <div className="row g-3">
          {searchMode === 'number' ? (
            <>
              <div className="col-12 col-md-8">
                <label htmlFor="ec-caseno" className="ec-label">Case number</label>
                <input id="ec-caseno" className="form-control ec-mono" value={caseNo} onChange={(e) => setCaseNo(e.target.value)}
                  placeholder="e.g. OS/1617/2020, CC/245/2023, WP(C)/456/2024" autoComplete="off" />
              </div>
              <div className="col-12 col-md-4 d-flex align-items-end">
                <div className="small ec-muted">
                  The same number exists in many courts. Use <strong className="ec-strong">State / Court</strong> on the left after searching to find yours.
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="col-12">
                <label htmlFor="ec-q" className="ec-label">Keyword</label>
                <input id="ec-q" className="form-control" value={query} onChange={(e) => setQuery(e.target.value)}
                  placeholder="e.g. specific performance, cheque bounce, Section 138" />
              </div>
              <div className="col-12 col-md-6">
                <label htmlFor="ec-sec" className="ec-label">Act & section (optional)</label>
                <input id="ec-sec" className="form-control" value={section} onChange={(e) => setSection(e.target.value)} placeholder="e.g. IPC 302, NI Act 138" />
              </div>
              <ChipField id="ec-lit" label="Litigant (either side)" values={litigants} onChange={setLitigants} placeholder="Name, press Enter" col="col-12 col-md-6" />
              <ChipField id="ec-adv" label="Advocate" values={advocates} onChange={setAdvocates} placeholder="Name, press Enter" col="col-12 col-md-6" />
              <ChipField id="ec-judge" label="Judge" values={judges} onChange={setJudges} placeholder="Name, press Enter" col="col-12 col-md-6" />
              <div className="col-12">
                <div className="ec-label">Name match</div>
                <Segmented value={mode} options={MATCH_MODES} onChange={setMode} label="Name match mode" />
              </div>
            </>
          )}

          <div className="col-12">
            <button type="button" className="btn btn-link ec-link p-0" onClick={() => setShowFilters((v) => !v)} aria-expanded={showFilters}>
              {showFilters ? '− Fewer filters' : '+ More filters'}
            </button>
          </div>

          {showFilters && (
            <>
              <div className="col-12 col-md-6 col-xl-3">
                <div className="ec-label">Status</div>
                <Segmented value={status} options={STATUSES} onChange={setStatus} label="Case status" />
              </div>
              <div className="col-6 col-md-3 col-xl-2">
                <label htmlFor="ec-type" className="ec-label">Case type</label>
                <select id="ec-type" className="form-select" value={caseType} onChange={(e) => setCaseType(e.target.value)}>
                  <option value="">Any</option>
                  {CASE_TYPES.map((t) => <option key={t} value={t}>{label('caseType', t)}</option>)}
                </select>
              </div>
              <div className="col-6 col-md-3 col-xl-2">
                <label htmlFor="ec-year" className="ec-label">Filing year</label>
                <input id="ec-year" className="form-control" inputMode="numeric" maxLength={4} value={filingYear}
                  onChange={(e) => setFilingYear(e.target.value.replace(/\D/g, ''))} placeholder="e.g. 2020" />
              </div>
              <div className="col-6 col-md-6 col-xl-3">
                <label htmlFor="ec-sort" className="ec-label">Sort by</label>
                <select id="ec-sort" className="form-select" value={sort} onChange={(e) => setSort(e.target.value)}>
                  {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <div className="col-6 col-md-6 col-xl-2">
                <label htmlFor="ec-size" className="ec-label">Per page</label>
                <select id="ec-size" className="form-select" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
                  {[20, 50, 100, 200].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            </>
          )}

          <div className="col-12 d-flex flex-wrap align-items-center gap-3">
            <span className="small ec-muted">Each search or page costs one charge. The same search within an hour is free.</span>
            <div className="ms-auto">
              <button type="submit" className="btn btn-ec px-4" disabled={loading}>{loading ? 'Searching…' : 'Search'}{!loading && <Cost kind="search" />}</button>
            </div>
          </div>
        </div>
      </form>

      <ErrorBox error={error} />

      {data && (
        <div className="row g-3">
          <aside className="col-12 col-lg-3">
            {FACET_GROUPS.map(({ facet, param, title }) => (facetValues(facet).length ? (
              <div className="ec-card ec-card-tight mb-3" key={facet}>
                <div className="ec-overline mb-2">{title}</div>
                {facetValues(facet).map(([code, count]) => {
                  const active = isPicked(param, code);
                  const name = label(facet, code);
                  return (
                    <button key={code} type="button" className={`ec-facet${active ? ' active' : ''}`} aria-pressed={active}
                      onClick={() => pickFacet(param, code)}>
                      <span className="ec-minw0">
                        {name}
                        {facet === 'courtCode' && name !== code && <span className="d-block ec-mono small ec-muted">{code}</span>}
                      </span>
                      <span className="ec-mono ec-muted">{Number(count).toLocaleString('en-IN')}</span>
                    </button>
                  );
                })}
                {facet === 'courtCode' && courtsFromPage && (
                  <div className="small ec-muted mt-2">Counted from this page. Pick a State first, or set Per page to 200, to see every court.</div>
                )}
              </div>
            ) : null))}
            {data.facetMode === 'full' && (
              <div className="small ec-muted">Too many matches to count by court. Add a filter or a filing year to narrow it.</div>
            )}
          </aside>

          <section className="col-12 col-lg-9">
            <div className="d-flex flex-wrap justify-content-between gap-2 ec-muted small mb-2">
              <span><strong className="ec-strong">{totalLabel}</strong> cases found{searchedFor ? <> for “{searchedFor}”</> : null}</span>
              <ChargeNote resp={data} />
            </div>
            {loading && <Loader label="Searching…" />}
            {!loading && (!data.results || !data.results.length) && (
              <Empty>
                {searchMode === 'number'
                  ? 'No case with this number. Try another format, e.g. O.S./1617/2020 or 1617/2020.'
                  : 'No cases match. Try fewer filters or Fuzzy match.'}
              </Empty>
            )}
            {!loading && (data.results || []).map((r) => {
              const pending = r.caseStatus === 'PENDING';
              const facts = [
                ['Case no.', r.registrationNumber || r.filingNumber],
                ['Court', r.courtCode && label('courtCode', r.courtCode) !== r.courtCode ? `${label('courtCode', r.courtCode)} (${r.courtCode})` : r.courtCode],
                ['Judge', list(r.judges)],
                ['Petitioner adv.', list(r.petitionerAdvocates)],
                ['Respondent adv.', list(r.respondentAdvocates)],
                ['Filed', fmtDate(r.filingDate)],
                [pending ? 'Next hearing' : 'Decided', fmtDate(pending ? r.nextHearingDate : r.decisionDate)],
                ['Category', r.caseCategory],
                ['Acts & sections', list(r.actsAndSections)],
              ].filter(([, v]) => v && v !== '—');
              return (
                <Link key={r.cnr || r.id} to={ecPath(`case/${r.cnr}`)} className="ec-result">
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <span className="ec-result-title">{caseTitle(r)}</span>
                    <span className={statusBadge(r.caseStatus)}>{label('caseStatus', r.caseStatus)}</span>
                    {r.caseType && <span className="ec-badge ec-badge-grey">{label('caseType', r.caseType)}</span>}
                  </div>
                  <div className="row g-1 small mt-2">
                    {facts.map(([k, v]) => (
                      <div className="col-12 col-md-6" key={k}>
                        <span className="ec-accent">{k}:</span> <span className="ec-strong">{v}</span>
                      </div>
                    ))}
                  </div>
                  {r.aiKeywords && r.aiKeywords.length > 0 && (
                    <div className="d-flex flex-wrap gap-1 mt-2">
                      {r.aiKeywords.slice(0, 5).map((k) => <span key={k} className="ec-tag">{k}</span>)}
                    </div>
                  )}
                  <div className="small ec-muted mt-2">Open full case history <Cost kind="case_detail" /></div>
                </Link>
              );
            })}
            {data.totalPages > 1 && (
              <div className="d-flex justify-content-center align-items-center gap-2 mt-3">
                <button type="button" className="btn btn-ec-outline btn-sm" disabled={page <= 1 || loading} onClick={() => run({ page: page - 1 })}>← Prev</button>
                <span className="ec-muted small">Page {page} of {data.totalPages}</span>
                <button type="button" className="btn btn-ec-outline btn-sm" disabled={!data.hasNextPage || loading} onClick={() => run({ page: page + 1 })}>Next →</button>
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}